declare const __FIREBASE_CONFIG__: string | undefined;

/** The subset of Firebase web config this widget reads. */
export type FirebaseConfig = {
  projectId: string;
  apiKey: string;
  appId: string;
  measurementId?: string;
};

/**
 * Absent locally, injected by App Hosting in production. When it is absent the
 * app runs in MOCK mode: no Firebase, no callables, no analytics. That is what
 * makes `npm run dev` work with no credentials and keeps dev traffic out of the
 * GA4 property.
 */
export function firebaseConfig(): FirebaseConfig | undefined {
  const raw = typeof __FIREBASE_CONFIG__ === "string" ? __FIREBASE_CONFIG__ : undefined;
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as FirebaseConfig;
  } catch {
    return undefined;
  }
}

/**
 * True when no Firebase config was injected, which is the local-dev case.
 *
 * A production bundle must never be mock: it once was, and answered every address
 * with a hardcoded fixture. `web/src/lib/viteDefine.test.ts` guards that.
 */
export const IS_MOCK = firebaseConfig() === undefined;

/**
 * `apphosting.yaml` holds the real reCAPTCHA site key as of 2026-07-30. Before
 * that it shipped the literal `REPLACE_WITH_RECAPTCHA_SITE_KEY`, and this guard
 * stays because a re-provisioned project starts from that placeholder again.
 * A placeholder is a non-empty string, so a bare truthiness check treats it as
 * present: `initializeAppCheck` would run with a garbage key and every callable
 * would 401 in production, with nothing catching it at build time or in CI.
 * Treating a `REPLACE_WITH_`-prefixed value as absent, same as an unset key, is
 * what makes that state visible.
 */
const rawRecaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined;

/** The site key, or undefined when unset **or** still the provisioning placeholder. */
export const RECAPTCHA_SITE_KEY =
  rawRecaptchaSiteKey && !rawRecaptchaSiteKey.startsWith("REPLACE_WITH_")
    ? rawRecaptchaSiteKey
    : undefined;

/**
 * App Hosting's injected config omits measurementId even with the GA4 stream
 * linked, so apphosting.yaml supplies it explicitly and this is the fallback
 * that reads it.
 */
export function measurementId(): string | undefined {
  return (
    firebaseConfig()?.measurementId ??
    (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined) ??
    undefined
  );
}

/** Linked from step 2. The 2026 policy covers subdomains; confirmed 2026-08-05. */
export const PRIVACY_URL = "https://kasselmansolar.com/privacy-policy-2026/";

/** Functions region. Must match REGION in functions/src/config.ts. */
export const FUNCTIONS_REGION = "us-east4";
